import { useQuery } from '@tanstack/react-query'
import { api, DashboardStats } from '../lib/api'
import { Building2, Users, CreditCard, Wallet, TrendingUp, Clock } from 'lucide-react'
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'

const formatCurrency = (pence: number) => 
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100)

export default function Dashboard() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: () => api.get<DashboardStats>('/dashboard/stats')
  })

  const { data: chartData } = useQuery({
    queryKey: ['dashboard-chart'],
    queryFn: () => api.get<{ date: string; volume: number }[]>('/dashboard/volume-chart')
  })

  if (isLoading) {
    return <div className="animate-pulse">Loading...</div>
  }

  const statCards = [
    { label: 'Companies', value: stats?.total_companies || 0, icon: Building2, color: 'bg-blue-500' },
    { label: 'Employees', value: stats?.total_employees || 0, icon: Users, color: 'bg-green-500' },
    { label: 'Active Cards', value: stats?.active_cards || 0, icon: CreditCard, color: 'bg-purple-500' },
    { label: 'Total Funded', value: formatCurrency(stats?.total_funded || 0), icon: Wallet, color: 'bg-amber-500' },
    { label: 'Monthly Volume', value: formatCurrency(stats?.monthly_volume || 0), icon: TrendingUp, color: 'bg-salad-500' },
    { label: 'Pending KYC', value: stats?.pending_kyc || 0, icon: Clock, color: 'bg-red-500' }
  ]

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {statCards.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-xl p-4 shadow-sm">
            <div className={`${color} w-10 h-10 rounded-lg flex items-center justify-center mb-3`}>
              <Icon className="text-white" size={20} />
            </div>
            <div className="text-2xl font-bold text-gray-900">{value}</div>
            <div className="text-sm text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      {/* Volume Chart */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Transaction Volume (30 days)</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData || []}>
              <defs>
                <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `£${v/100}`} />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Area type="monotone" dataKey="volume" stroke="#22c55e" fill="url(#volumeGradient)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}
